/**
 * <ts-card-swatches> — in-place colorway selection on product cards.
 *
 * On this store each colorway is a separate product, so `ts-color-swatches`
 * normally renders sibling colors as links to their PDPs. On a product card
 * that navigation is wrong — selecting a color should update the card in
 * place, matching the Searchspring PLP card (`ts-searchspring-product-card.js`
 * `#selectSwatch`). The `selectable` mode of `snippets/ts-color-swatches.liquid`
 * renders the siblings as buttons carrying the data this component needs
 * (image, title, price, URL) and wraps them in this element.
 *
 * Selecting a swatch swaps the host card's image, title, price, and overlay
 * link. The Quick View trigger resolves its product URL from that link at
 * click time, so retargeting it also retargets Quick View to the selected
 * colorway.
 */

/** Card roots this component knows how to update. */
const CARD_SELECTOR = '.ts-product-card, .ts-essential-features__card';

class TsCardSwatches extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    if (!target || typeof target.closest !== 'function') return;

    const swatch = /** @type {HTMLButtonElement | null} */ (
      target.closest('button.variant-option__button-label--has-swatch')
    );
    if (!swatch || !this.contains(swatch)) return;

    this.#select(swatch);
    // A pointer click would leave focus on the pip, holding the card's
    // :focus-within hover states open; keyboard activation (detail === 0)
    // keeps focus for continued navigation.
    if (/** @type {MouseEvent} */ (event).detail > 0) swatch.blur();
  };

  /**
   * Swap the host card's image, title, price, and links to the colorway.
   * @param {HTMLButtonElement} swatch
   */
  #select(swatch) {
    const card = this.closest(CARD_SELECTOR);
    if (!card) return;

    const { image, srcset, title, price, url } = swatch.dataset;

    const img = /** @type {HTMLImageElement | null} */ (
      card.querySelector('.ts-product-card__media img, img.ts-essential-features__card-img')
    );
    if (img && image) {
      img.src = image;
      if (srcset) img.srcset = srcset;
      if (title) img.alt = title;
      // A <picture> wrapper's <source> elements would override the img's
      // src/srcset — point them at the colorway image too.
      if (srcset) {
        for (const source of card.querySelectorAll('.ts-product-card__media source')) {
          source.setAttribute('srcset', srcset);
        }
      }
    }

    if (title) {
      const titleElement = card.querySelector('.ts-product-card__title, .ts-essential-features__card-title');
      if (titleElement) titleElement.textContent = title;
    }

    if (price) {
      const priceElement = /** @type {HTMLElement | null} */ (
        card.querySelector('.ts-product-card__price, .ts-essential-features__card-price')
      );
      if (priceElement) this.#applyPrice(priceElement, price, swatch.dataset.compareAtPrice);
    }

    if (url) {
      const link = /** @type {HTMLAnchorElement | null} */ (
        card.querySelector('.product-card__link, .ts-essential-features__card-link')
      );
      if (link) {
        link.href = url;
        // The overlay link's hidden text is the card's accessible name.
        const hiddenTitle = link.querySelector('.visually-hidden');
        if (hiddenTitle && title) hiddenTitle.textContent = title;
      }
    }

    for (const other of this.querySelectorAll('button.variant-option__button-label--has-swatch')) {
      other.setAttribute('aria-pressed', other === swatch ? 'true' : 'false');
    }
  }

  /**
   * Render the colorway's price — struck compare-at + sale price when the pip
   * carries `data-compare-at-price`, plain text otherwise. Mirrors the
   * server-rendered ts-product-card sale markup so swaps stay in sync.
   * @param {HTMLElement} priceElement
   * @param {string} price
   * @param {string} [compareAtPrice]
   */
  #applyPrice(priceElement, price, compareAtPrice) {
    priceElement.classList.toggle('ts-product-card__price--on-sale', Boolean(compareAtPrice));

    if (!compareAtPrice) {
      priceElement.textContent = price;
      return;
    }

    // Text nodes only — prices and labels come from data attributes and must
    // never be parsed as HTML.
    const compare = document.createElement('s');
    compare.className = 'ts-product-card__price-compare';
    compare.append(this.#hiddenLabel(priceElement.dataset.labelRegular), compareAtPrice);

    const current = document.createElement('span');
    current.className = 'ts-product-card__price-current';
    current.append(this.#hiddenLabel(priceElement.dataset.labelSale), price);

    priceElement.replaceChildren(compare, current);
  }

  /**
   * Visually-hidden screen-reader label ("Regular price " / "Sale price ").
   * @param {string} [label] - Translated label from the price element's data attributes.
   */
  #hiddenLabel(label) {
    const span = document.createElement('span');
    span.className = 'visually-hidden';
    if (label) span.textContent = `${label} `;
    return span;
  }
}

if (!customElements.get('ts-card-swatches')) {
  customElements.define('ts-card-swatches', TsCardSwatches);
}
